/**
 * useReauditReconciliation
 *
 * Reconciles card states in Redux when audit data refreshes (manual reanalyze
 * or SSE COMPLIANCE_EVALUATED). Prevents stale card states from accumulating
 * when the compliance status of rules changes after a reaudit.
 */

import { useEffect, useRef } from 'react';
import { useAppDispatch } from '@store/hooks/core';

import type { CardState } from '../store/auditUiSlice';
import { resetCardState } from '../store/auditUiSlice';
import type { RuleComplianceAssessment } from '../types';

export function useReauditReconciliation(
  assessments: RuleComplianceAssessment[],
  cardStates: Record<string, CardState>,
): void {
  const dispatch = useAppDispatch();
  const prevAssessmentsRef = useRef(assessments);

  useEffect(() => {
    // Only run when assessments reference changes (new data from reaudit)
    if (assessments === prevAssessmentsRef.current) return;
    prevAssessmentsRef.current = assessments;

    const complianceMap = new Map(assessments.map(a => [a.rule_id, a.compliant]));

    for (const [ruleId, cardState] of Object.entries(cardStates)) {
      // Never touch cards that are mid-update — the middleware handles those
      if (cardState.status === 'updating') continue;

      if (cardState.status === 'completed') {
        const compliant = complianceMap.get(ruleId);
        // Fix didn't resolve it — reset so user can try again
        if (compliant === 'no' || compliant === 'warning') {
          dispatch(resetCardState(ruleId));
        }
      }

      if (cardState.status === 'ignored') {
        const compliant = complianceMap.get(ruleId);
        // Rule resolved itself or was removed — clean up
        if (compliant === 'yes' || !complianceMap.has(ruleId)) {
          dispatch(resetCardState(ruleId));
        }
      }
    }

    // Clean up card states for rules no longer in assessments
    for (const ruleId of Object.keys(cardStates)) {
      if (cardStates[ruleId].status === 'updating') continue;
      if (!complianceMap.has(ruleId)) {
        dispatch(resetCardState(ruleId));
      }
    }
  }, [assessments, cardStates, dispatch]);
}
